import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLock, FiEye, FiEyeOff, FiLoader, FiAlertCircle, FiCheckCircle, FiHash, FiClock } = FiIcons;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [timeLeft, setTimeLeft] = useState(180); // 3 minutes

  const { verifyOtp, updatePassword } = useSupabaseAuth();

  useEffect(() => {
    if (!email) {
      navigate('/forgot-password');
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [email, navigate]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      setError('Please enter the 6-digit PIN');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (timeLeft === 0) {
      setError('PIN has expired. Please request a new one.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // 1. Verify OTP
      const { error: verifyError } = await verifyOtp(email, otp);
      if (verifyError) throw verifyError;

      // 2. Update Password (user is now signed in temporarily)
      const { error: updateError } = await updatePassword(password);
      if (updateError) throw updateError;

      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Invalid or expired code.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-gray-50 py-12 px-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full space-y-8 bg-white p-8 md:p-10 rounded-[2.5rem] shadow-xl border border-gray-100">
        <div className="text-center">
          <div className="w-20 h-20 bg-emerald-600 rounded-3xl flex items-center justify-center p-4 mx-auto mb-6 shadow-xl shadow-emerald-100/50">
            <SafeIcon icon={FiLock} className="text-white text-4xl" />
          </div>
          <h2 className="text-3xl font-black text-gray-900 tracking-tight">Verify PIN</h2>
          <p className="mt-2 text-sm text-gray-500 font-medium leading-relaxed">
            Enter the code sent to <span className="font-bold text-slate-900">{email}</span>
          </p>
        </div>

        {success ? (
          <div className="text-center space-y-4 py-4">
            <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto">
              <SafeIcon icon={FiCheckCircle} className="text-4xl text-emerald-600" />
            </div>
            <p className="font-black text-gray-900">Password Updated!</p>
            <p className="text-sm text-gray-500">Redirecting you to login...</p>
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-2xl flex items-start text-sm">
                <SafeIcon icon={FiAlertCircle} className="mt-0.5 mr-2 flex-shrink-0" />
                <span className="font-bold">{error}</span>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-2 ml-1">
                  <label className="block text-xs font-black text-gray-400 uppercase tracking-widest">6-Digit PIN</label>
                  <span className={`text-[10px] font-black uppercase flex items-center ${timeLeft < 30 ? 'text-red-500' : 'text-indigo-500'}`}>
                    <SafeIcon icon={FiClock} className="mr-1" /> Expiring in {formatTime(timeLeft)}
                  </span>
                </div>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <SafeIcon icon={FiHash} className="text-gray-400 group-focus-within:text-emerald-500 transition-colors" />
                  </div>
                  <input 
                    type="text" 
                    required 
                    maxLength={6}
                    value={otp} 
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} 
                    className="pl-11 tracking-[0.5em] block w-full px-4 py-4 bg-gray-50 border border-transparent rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:bg-white outline-none transition-all font-black text-lg" 
                    placeholder="000000" 
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">New Password</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <SafeIcon icon={FiLock} className="text-gray-400 group-focus-within:text-emerald-500 transition-colors" />
                  </div>
                  <input 
                    type={showPassword ? 'text' : 'password'} 
                    required 
                    value={password} 
                    onChange={(e) => setPassword(e.target.value)} 
                    className="pl-11 pr-12 block w-full px-4 py-4 bg-gray-50 border border-transparent rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:bg-white outline-none transition-all font-medium" 
                    placeholder="••••••••" 
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-4 flex items-center">
                    <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="text-gray-400 hover:text-emerald-600 transition-colors" />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Confirm New Password</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <SafeIcon icon={FiLock} className="text-gray-400 group-focus-within:text-emerald-500 transition-colors" />
                  </div>
                  <input 
                    type="password" 
                    required 
                    value={confirmPassword} 
                    onChange={(e) => setConfirmPassword(e.target.value)} 
                    className="pl-11 block w-full px-4 py-4 bg-gray-50 border border-transparent rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:bg-white outline-none transition-all font-medium" 
                    placeholder="••••••••" 
                  />
                </div>
              </div>
            </div>

            <button 
              type="submit" 
              disabled={loading || timeLeft === 0} 
              className="w-full flex justify-center py-4 px-4 bg-emerald-600 text-white text-sm font-black rounded-2xl hover:bg-emerald-700 disabled:opacity-50 transition-all shadow-xl shadow-emerald-100 transform active:scale-[0.98]"
            >
              {loading ? <SafeIcon icon={FiLoader} className="animate-spin mr-2" /> : 'Set New Password'}
            </button>

            <div className="text-center">
              <button 
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-xs font-black text-slate-400 hover:text-indigo-600 uppercase tracking-widest transition-colors"
              >
                Request a new PIN
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
};

export default ResetPasswordPage;